import {
    QueryClient,
    QueryFunctionContext,
    UseMutationOptions,
    UseMutationResult,
    useQueryClient,
    UseQueryOptions,
} from '@tanstack/react-query';
import { ChangeEvent, ChangeEventService, Identified } from '@faro/realtime-services';
import { type DefaultError, QueryKey } from '@tanstack/query-core';
import { useRealtimeMutation } from './useRealtimeMutation';
import { OptimisticUpdateContext, useRealtimeQuery, UseRealtimeQueryResult } from './useRealtimeQuery';
import { handleMutationContext } from './RealtimeEntityDataHookUtils';

/**
 * Cache data getter function.
 * If parameter is provided then it will return the entity with the given id from the cache.
 * Otherwise, it will return the list of entities from the cache.
 */
export interface CacheDataGetter<TEntity> {
    (id: unknown): TEntity;
    (): TEntity[];
}

export type ConsumerQueryOptions<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
> = Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;

export type ListFunction<TResult, TParameters> = (parameters: TParameters, signal: AbortSignal) => Promise<TResult[]>;
export type ListHook<TEntity, TParameters> = (
    parameters: TParameters,
    options?: ConsumerQueryOptions<TEntity[]>
) => UseRealtimeQueryResult<TEntity[]>;

export type GetFunction<TResult, TParameters extends Identified> = (
    parameters: TParameters,
    signal: AbortSignal
) => Promise<TResult>;
export type GetHook<TEntity, TParameters extends Identified> = (
    parameters: TParameters,
    options?: ConsumerQueryOptions<TEntity>
) => UseRealtimeQueryResult<TEntity>;

/**
 * Options available when configuring a mutation hook.
 * Generally mirrors the options available in useMutation from react-query.
 */
export type ConsumerMutationOptions<
    TData = unknown,
    TError = DefaultError,
    TVariables = void,
    TContext = unknown,
> = Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'scope' | 'mutationFn'>;

type OptimisticCallbackData = { scopeName: string; scopeId: string };
/**
 * An optimistic update callback that updates associated
 * {@link RealtimeEntityDataHookFactory#get} or {@link RealtimeEntityDataHookFactory#list} results
 * with updated data BEFORE the API call is resolved and data is re-fetched.
 *
 * @param mutationData  The data that will be sent to the API
 * @param cachedData    The data that is currently in the QueryClient cache with the matching params/query-key
 * @param client        The QueryClient instance
 * @param context       The context object with the scopeName and scopeId
 * @returns The updated data that will be set in the cache before the API call is resolved
 */
type OptimisticCallback<TInput, TCacheData> = (
    mutationData: TInput,
    cachedData: TCacheData,
    client: QueryClient,
    context: OptimisticCallbackData
) => TCacheData;

/**
 * Options available when configuring a mutation hook.
 */
export type MutateOptions<TParameters, TOptimisticCacheData> = {
    /**
     * An optional optimistic update callback
     */
    optimisticUpdate?: OptimisticCallback<TParameters, TOptimisticCacheData>;
};
export type MutateFunction<TResult, TParameters> = (parameters: TParameters) => Promise<TResult>;

/**
 * A custom hook that mutates data given the provided mutateFunction.
 * For a proper work of optimistic updates, the params should match one from the data retrieval hook.
 * The provided hook function will automatically roll back optimistic updates on error.
 *
 * @param params    The parameters to use for optimistic updates (must match the data retrieval hook params)
 *                  (e.g. { studyId: '123', scheduleOfActivitiesId: '456' })
 * @param options   The mutation options
 */
export type MutateHook<TResult, TParameters, TEntity> = (
    scope: Partial<TParameters & TEntity>,
    options?: ConsumerMutationOptions<TResult, unknown, TParameters>
) => UseMutationResult<TResult, unknown, TParameters>;

/**
 * Options available when configuring a RealtimeEntityDataHookFactory
 */
export interface RealtimeEntityHookFactoryOptions<TEvent extends ChangeEvent, TScopeParameters> {
    /**
     * The name of the root entity type that the scope (scopeId) refers to (e.g. "study" or "study-space")
     * This is for query cache data readability during troubleshooting
     */
    scopeName: string;

    /**
     * Gets the scope ID from the given query params (e.g. x => x.studyId)
     * @param params The parameters to get the scope ID from
     */
    getScopeId: (params: TScopeParameters) => string;

    /**
     * Change service provider.
     * This function can use a hook inside to get the service.
     */
    getChangeEventService: () => ChangeEventService<TEvent>;

    /**
     * The entity type identifier (e.g. Activity, Epoch, etc...)
     */
    type: string;
}

/**
 * This factory generates data-fetching hooks for a Study Designer or Study Space "Entity Type."
 *
 * The hooks are configured with "realtime" characteristics such as:
 * - automatically re-fetching when the receiving change events that include the target entity type
 * - allowing consumers to update the query cache optimistically and roll back on request failure
 * - read the currently-cached data without triggering an API call
 *
 * @template TEvent             the type of change event that the factory will listen for
 *                              (e.g. ChangeEvent)
 * @template TEntityParameters  the type parameters that uniquely identify the response entity(ies) in the query cache
 *                              these parameters are passed to the client functions representing the API calls
 *                              (e.g. { studyId: string } or { studyId: string, epochId: string } or ...)
 * @template TEntity            the type of entity that the factory will fetch data for
 *                              (e.g. StudyEntityType.Activity)
 *
 * Note that this is factory for generating wrappers around React-Query's useQuery and useMutation hooks.
 * @see https://tanstack.com/query/latest/docs/framework/react/typescript
 *
 * @example
 * ```typescript
 * // This example will use optimistic updates to update the cache with the new entity data
 * // MyEntityHooks.ts
 * import { createRealtimeEntityDataHookFactory } from './RealtimeEntityDataHookFactory';
 * import { MyEntity, toMyEntity } from '@faro/study-designer-model';
 * import { myEntityClient } from './MyEntityClient';
 *
 * const factory = createStudyEntityHookFactory<MyEntity>(StudyEntityType.MyEntity);
 * export const useMyEntities = factory.list((query, signal) =>
 *     axios.get('/api/my-entity', { params: query, signal }).then(response => response.data?.map(toMyEntity) ?? [])
 * );
 * export const useMyEntityUpdate = factory.mutate(
 *     (entity, signal) => axios.put(`/api/my-entity/${entity.id}`, entity, { signal }).then(response => toMyEntity(response.data)),
 *     {
 *         optimisticUpdate: (entity, entities) => entities.map(x => (x.id === entity.id ? entity : x)),
 *     }
 * );
 *
 * //MyEntityComponent.ts
 * import React, { JSX, ComponentProps } from 'react';
 * import { useMyEntities, useMyEntityUpdate } from './MyEntityHooks';
 *
 * function MyEntityComponent(props: ComponentProps<'div'>): JSX.Element {
 *   const myEntitiesHook = useMyEntities({ studyId: props.studyId, parentId: props.parentId});
 *   const updateEntity = useMyEntityUpdate({ studyId: props.studyId, parentId: props.parentId});
 *
 *   const onUpdate = (entity: MyEntity) => updateEntity.mutateAsync(entity).catch(showErrorHandler('Failed to update activity'));
 *
 *   return (
 *     <div {...props}>
 *          <...
 *             entities={myEntitiesHook.data}
 *             onUpdate={onUpdate}
 *          />
 *     </div>
 *   );
 * }
 * ```
 */
export class RealtimeEntityDataHookFactory<TEvent extends ChangeEvent, TEntityParameters, TEntity> {
    constructor(private readonly config: RealtimeEntityHookFactoryOptions<TEvent, TEntityParameters>) {}

    /**
     * Creates and returns a custom hook that fetches data with the provided listFunction.
     * The data is re-fetched when change events with the same scopeId and entityType are encountered.
     *
     * @template TQueryParameters   The type of query parameters that the client function expects
     *                              (e.g. { scheduleOfActivitiesId: string })
     *
     * @param listFunction          The API client list function
     *                              (e.g. activityClient.listActivities)
     * @param dependentEntityTypes  Optional list of 'other' change event entity types (in addition to the factory
     *                              TEntity) that should trigger data re-fetching
     * @return useRealtimeQuery     An extended React-Query useQuery hook that fetches data and automatically re-fetches when
     *                              specified change events occur
     */
    list<TQueryParameters>(
        listFunction: ListFunction<TEntity, TEntityParameters & TQueryParameters>,
        dependentEntityTypes?: string[]
    ): ListHook<TEntity, TEntityParameters & TQueryParameters> {
        const { config } = this;

        return (params: TEntityParameters & TQueryParameters, options?: ConsumerQueryOptions<TEntity[]>) => {
            const changeEventService = config.getChangeEventService();
            const events = changeEventService.events;
            const scopeId = config.getScopeId(params);
            const queryKey = [config.scopeName, scopeId, config.type, params];
            const queryFn = async ({ signal }: QueryFunctionContext) => {
                const revision = await changeEventService.latestRevisionCache?.get(scopeId);
                const paramsWithRevision = revision != null ? { ...params, revision } : params;
                return await listFunction(paramsWithRevision, signal);
            };

            return useRealtimeQuery({
                ...options,
                queryKey,
                queryFn,
                events,
                refetchWhen: event =>
                    event.scope === scopeId &&
                    (event.type === config.type || (dependentEntityTypes ?? []).includes(event.type)),
            });
        };
    }

    /**
     * Creates and returns a custom hook that fetches a single entity by id with the provided getFunction.
     * The data is re-fetched when change events with the same id field are encountered
     *
     * @param getFunction       The API client get function
     *                          (e.g. studyClient.getStudy)
     * @return useRealtimeQuery An extended React-Query useQuery hook that fetches data and automatically re-fetches
     *                          when specified change events occur
     */
    get(
        getFunction: GetFunction<TEntity, TEntityParameters & Identified>
    ): GetHook<TEntity, TEntityParameters & Identified> {
        const { config } = this;

        return (params, options) => {
            const changeEventService = config.getChangeEventService();
            const events = changeEventService.events;
            const scopeId = config.getScopeId(params);
            const queryKey = [config.scopeName, scopeId, config.type, params.id];
            const queryFn = async ({ signal }: QueryFunctionContext) => {
                const revision = await changeEventService.latestRevisionCache?.get(scopeId);
                const paramsWithRevision = revision != null ? { ...params, revision } : params;
                return await getFunction(paramsWithRevision, signal);
            };

            return useRealtimeQuery({
                ...options,
                queryKey,
                queryFn,
                events,
                refetchWhen: event => event.id === params.id,
            });
        };
    }

    /**
     * Creates and returns a custom hook that mutates data given the provided client mutateFunction.
     * The provided hook function will automatically roll back optimistic updates on error.
     *
     * @template TResult                The type of the result of the mutation function
     * @template TParameters            The type of the parameters that the client function expects
     *                                  Note that these parameters should match the parameters of the data retrieval
     *                                  hook (list, get) for optimistic updates to work correctly
     *                                  (e.g. { studyId: string, scheduleOfActivitiesId: string })
     * @template TOptimisticCacheData   The type of the data that is currently in the QueryClient cache from the data
     *                                  retrieval hook with the matching params/query-key
     *
     * @param mutateFunction    The API client data mutation function
     *                          (e.g. activityClient.update)
     * @param mutationOptions   Mutation options such as whether to optimistically update during the API client call
     */
    mutate<TResult, TParameters extends TEntityParameters = TEntityParameters, TOptimisticCacheData = TParameters[]>(
        mutateFunction: MutateFunction<TResult, TParameters>,
        mutationOptions: MutateOptions<TParameters, TOptimisticCacheData> = {}
    ): MutateHook<TResult, TParameters, TEntity> {
        const { config } = this;

        return (params, options) => {
            const queryClient = useQueryClient();
            const service = config.getChangeEventService();
            const scopeId = config.getScopeId(params as Required<TParameters>);
            const mutationKey = [config.scopeName, scopeId, config.type];
            const optimisticUpdate = mutationOptions?.optimisticUpdate;

            return useRealtimeMutation({
                ...options,
                service,
                scope: scopeId,
                mutationKey,
                mutationFn: mutateFunction,
                onMutate: async vars => {
                    // no implementation, if optimisticUpdate enabled it will be overwritten in the conditional block
                    let rollback: (() => void) | undefined;
                    if (optimisticUpdate) {
                        const key = [config.scopeName, scopeId, config.type, params];
                        const data = queryClient.getQueryData<TOptimisticCacheData>(key);
                        rollback = () => {
                            queryClient.setQueryData(key, data);
                        };
                        if (!data) {
                            throw new Error("Optimistic query can't extract data from cache");
                        }
                        const result = optimisticUpdate(vars, data, queryClient, {
                            scopeName: config.scopeName,
                            scopeId,
                        });
                        queryClient.setQueryData(key, result);
                    }

                    return handleMutationContext(vars, () => vars, rollback);
                },
                onError: (error, variables, context) => {
                    // Rollback optimistic updates on mutation errors
                    (context as OptimisticUpdateContext)?.rollback?.();

                    return options?.onError?.(error, variables, context);
                },
            });
        };
    }

    /**
     * Create and returns a custom hook that is a getter for the current state of a data retrieval hook
     * (list or get) in the QueryClient cache.
     * If parameter is provided then it will return the entity with the given id from the cache.
     * Otherwise, it will return the list of entities from the cache.
     *
     * @template TParameters    The type of parameters of the associated data retrieval hook
     */
    getState<TParameters extends TEntityParameters = TEntityParameters>() {
        const { config } = this;
        return (params: TParameters): CacheDataGetter<TEntity> => {
            const queryClient = useQueryClient();
            function getData(id: unknown): TEntity;
            function getData(): TEntity[];
            function getData(id?: unknown): TEntity | TEntity[] {
                const scopeId = config.getScopeId(params);
                const key = [config.scopeName, scopeId, config.type, id ?? params];
                const data = queryClient.getQueryData<TEntity>(key);
                if (!data) {
                    throw new Error("Can't extract data from cache");
                }
                return data;
            }

            return getData;
        };
    }
}
